/**
 * Minimal toast notifications: toast('Saved') from anywhere, rendered by <Toaster/> once in App.
 */
import { CheckCircle2, Info, XCircle } from 'lucide-react';
import { create } from 'zustand';

type Tone = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  message: string;
  tone: Tone;
}

const useToasts = create<{ items: ToastItem[] }>(() => ({ items: [] }));
let nextId = 1;

export function toast(message: string, tone: Tone = 'success'): void {
  const id = nextId++;
  useToasts.setState((s) => ({ items: [...s.items, { id, message, tone }] }));
  window.setTimeout(() => useToasts.setState((s) => ({ items: s.items.filter((t) => t.id !== id) })), 3500);
}

const ICON = { success: CheckCircle2, error: XCircle, info: Info } as const;
const COLOR = { success: 'text-ok', error: 'text-danger', info: 'text-mute' } as const;

export function Toaster() {
  const items = useToasts((s) => s.items);
  return (
    <div aria-live="polite" className="pointer-events-none fixed right-4 bottom-4 z-[60] flex flex-col gap-2">
      {items.map((t) => {
        const Icon = ICON[t.tone];
        return (
          <div
            key={t.id}
            className="pointer-events-auto flex animate-fade-in items-center gap-2.5 rounded-xl border border-line bg-canvas px-4 py-3 text-sm shadow-lg"
          >
            <Icon size={18} className={COLOR[t.tone]} />
            {t.message}
          </div>
        );
      })}
    </div>
  );
}
